import { useEffect, useState } from "react";
import { X, Download, CheckCircle2, XCircle, RefreshCw, FileText, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

/**
 * Admin viewer + reviewer for a single document.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - doc: { doc_id, doc_type, file_name, file_url, status, number, expiry_date, rejection_reason, ... }
 *  - workerName / ownerName (optional, just for header)
 *  - onReview: async (action, reason) => boolean (true if succeeded)
 */
export default function DocumentViewerModal({ open, onClose, doc, ownerName, onReview }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null); // 'approve' | 'reject' | 'resubmit'

  useEffect(() => {
    if (open) setReason(doc?.rejection_reason || "");
  }, [open, doc]);

  if (!open || !doc) return null;

  const fileUrl = doc.file_url || "";
  const fileName = doc.file_name || `${doc.doc_type || "document"}`;
  const isImage = /^data:image\//i.test(fileUrl) || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(fileName);
  const isPdf = /^data:application\/pdf/i.test(fileUrl) || /\.pdf$/i.test(fileName);
  const niceType = (doc.doc_type || "Document").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const statusBadge = (s) => {
    const cls = s === "verified" || s === "approved" ? "bg-emerald-100 text-emerald-700"
      : s === "rejected" ? "bg-rose-100 text-rose-700"
      : s === "resubmit_requested" ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-700";
    return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${cls}`}>{(s || "pending").toUpperCase()}</span>;
  };

  const run = async (action) => {
    if (action !== "approve" && !reason.trim()) {
      // require a reason for reject/resubmit
      alert(`Please add a note for the worker before ${action === "reject" ? "rejecting" : "requesting a resubmit"}.`);
      return;
    }
    setBusy(action);
    const ok = await onReview(action, action === "approve" ? "" : reason.trim());
    setBusy(null);
    if (ok) onClose();
  };

  const downloadFile = () => {
    if (!fileUrl) return;
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="doc-viewer-modal"
    >
      <div
        className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4 text-[#5BA3C0]" />
              <h3 className="font-semibold text-slate-900 truncate text-[15px]">{niceType}</h3>
              {statusBadge(doc.status)}
            </div>
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {ownerName ? <span className="font-medium">{ownerName}</span> : null}
              {ownerName ? " · " : ""}
              {fileName}
              {doc.number ? ` · #${doc.number}` : ""}
              {doc.expiry_date ? ` · Expires ${doc.expiry_date}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {fileUrl && (
              <Button variant="outline" size="sm" onClick={downloadFile} data-testid="doc-viewer-download" className="h-8 text-xs">
                <Download className="w-3.5 h-3.5 mr-1" /> Download
              </Button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
              aria-label="Close"
              data-testid="doc-viewer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-3">
          {!fileUrl ? (
            <div className="text-center text-slate-500 py-16">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No file attached for this document yet.</p>
            </div>
          ) : isImage ? (
            <img src={fileUrl} alt={niceType} className="max-h-[60vh] max-w-full rounded-lg shadow-sm bg-white" />
          ) : isPdf ? (
            <iframe
              src={fileUrl}
              title={`${niceType} preview`}
              className="w-full h-[60vh] bg-white rounded-lg border-0"
            />
          ) : (
            <div className="text-center py-12 px-6 max-w-md">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-400" />
              <p className="text-sm text-slate-700 font-medium mb-1">{fileName}</p>
              <p className="text-xs text-slate-500 mb-4">This file type can't be previewed inline. Use the download button to view it.</p>
              <Button onClick={downloadFile} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white">
                <Download className="w-4 h-4 mr-1.5" /> Download file
              </Button>
            </div>
          )}
        </div>

        {/* Review controls */}
        <div className="border-t border-slate-100 px-5 py-4 bg-white space-y-3">
          <label className="block">
            <span className="text-[12px] font-semibold text-slate-700">
              Reviewer note <span className="text-slate-400 font-normal">(required to reject or request a resubmit — emailed to worker)</span>
            </span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Document is blurry — please re-upload a clearer photo of both sides."
              className="mt-1.5 min-h-[68px] text-sm"
              data-testid="doc-viewer-reason"
            />
          </label>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              className="h-9 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
              disabled={!!busy}
              onClick={() => run("resubmit")}
              data-testid="doc-viewer-resubmit"
            >
              {busy === "resubmit" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Request resubmit
            </Button>
            <Button
              variant="outline"
              className="h-9 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
              disabled={!!busy}
              onClick={() => run("reject")}
              data-testid="doc-viewer-reject"
            >
              {busy === "reject" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
              Reject
            </Button>
            <Button
              className="h-9 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
              disabled={!!busy}
              onClick={() => run("approve")}
              data-testid="doc-viewer-approve"
            >
              {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Approve
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
